import React from 'react';
import { Drawer, Button, Space, Tag, Progress, Descriptions, Typography, message } from 'antd';
import { DownloadOutlined, BuildOutlined } from '@ant-design/icons';
import TargetTags from './TargetTags';
import { STAGE_NAME, STAGE_VERSION, BUILD_LOGS, buildJobs } from './buildConst';

const { Text } = Typography;

// 构建 App 抽屉（展示型）：进度与状态由外部管理，点击「重新构建」回调 onRebuild
const BuildDrawer = ({ open, record, stage, progress, stateVal, onClose, onRebuild }) => {
    const rec = record;
    const jobs = rec ? buildJobs(rec) : [];
    const per = jobs.length ? 100 / jobs.length : 100;
    const done = stateVal === 'done';
    const failed = stateVal === 'failed';
    const logCount = progress >= 100 ? BUILD_LOGS.length : Math.min(BUILD_LOGS.length, Math.floor(progress / 25) + (progress > 0 ? 1 : 0));

    return (
        <Drawer
            title={`构建 App · ${rec?.appName || ''}${stage ? ` · ${STAGE_NAME[stage]}` : ''}`}
            open={open}
            width={620}
            onClose={onClose}
            extra={(done || failed) && <Button icon={<BuildOutlined />} onClick={onRebuild}>重新构建</Button>}
        >
            {rec && (
                <>
                    <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
                        <Descriptions.Item label="应用名称">{rec.appName}</Descriptions.Item>
                        <Descriptions.Item label="所属企业">{rec.companyName}</Descriptions.Item>
                        <Descriptions.Item label="构建阶段">{STAGE_NAME[stage]}</Descriptions.Item>
                        <Descriptions.Item label="定制范围"><TargetTags targets={rec.targets} /></Descriptions.Item>
                    </Descriptions>

                    <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong>{done ? '构建完成' : failed ? '构建失败' : '构建中…'}</Text>
                        <Tag color={done ? 'success' : failed ? 'error' : 'processing'} style={{ border: 'none' }}>{progress}%</Tag>
                    </div>
                    <Progress percent={progress} status={done ? 'success' : failed ? 'exception' : 'active'} style={{ marginBottom: 16 }} />

                    <div style={{ marginBottom: 8 }}><Text strong>构建任务（按平台 × 区域）</Text></div>
                    {jobs.map((j, i) => {
                        const jp = Math.min(100, Math.max(0, Math.round(((progress - i * per) / per) * 100)));
                        const jobFailed = failed && i === Math.min(jobs.length - 1, Math.floor(progress / per));
                        const jobDone = jp >= 100 && !jobFailed;
                        const building = !jobDone && !jobFailed && progress > i * per;
                        return (
                            <div key={j.key} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                    <Space size={8}>
                                        <Text style={{ fontSize: 13 }}>{j.label}</Text>
                                        <Tag style={{ border: 'none' }}>{j.artifact}</Tag>
                                    </Space>
                                    <Tag color={jobFailed ? 'error' : jobDone ? 'success' : building ? 'processing' : 'default'} style={{ border: 'none' }}>
                                        {jobFailed ? '失败' : jobDone ? '已完成' : building ? '构建中' : '等待中'}
                                    </Tag>
                                </div>
                                <Progress percent={jp} size="small" status={jobFailed ? 'exception' : jobDone ? 'success' : 'active'} />
                            </div>
                        );
                    })}

                    <div style={{ marginTop: 16, marginBottom: 8 }}><Text strong>构建日志</Text></div>
                    <div style={{ background: '#1e1e1e', color: '#d4d4d4', borderRadius: 8, padding: 12, fontFamily: 'monospace', fontSize: 12, lineHeight: 1.9, minHeight: 120 }}>
                        {BUILD_LOGS.slice(0, logCount).map((l) => <div key={l}>{l}</div>)}
                        {failed && <div style={{ color: '#ff7875' }}>ERROR: iOS 打包签名失败（Provisioning Profile 与 Bundle ID 不匹配）</div>}
                        {!done && !failed && <div style={{ color: '#888' }}>正在构建，请稍候…</div>}
                    </div>

                    {failed && (
                        <div style={{ marginTop: 16, padding: 16, background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8 }}>
                            <Text strong type="danger">构建失败</Text>
                            <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                                失败原因：iOS 打包签名失败（Provisioning Profile 与应用 Bundle ID 不匹配）。请检查证书 / 描述文件后重新构建。
                            </Text>
                        </div>
                    )}

                    {done && (
                        <div style={{ marginTop: 16, padding: 16, background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8 }}>
                            <Text strong style={{ color: '#237804' }}>构建产物（{STAGE_VERSION[stage]}）</Text>
                            {jobs.map((j) => (
                                <div key={j.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                                    <Text style={{ fontSize: 13 }}>{j.label} · {j.artifact}</Text>
                                    <Button type="link" size="small" icon={<DownloadOutlined />}
                                        onClick={() => message.success(`开始下载 ${j.label} 产物（mock）`)}>下载</Button>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </Drawer>
    );
};

export default BuildDrawer;
